/**
 * Validation Summary Component
 * 
 * Displays validation results in a clear, organized format with expandable details.
 * Shows overall status, counts, and per-route validation information.
 * 
 * @module ValidationSummary
 */

import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
/**
 * Validation error or warning for a specific route
 */
export interface RouteValidationIssue {
  /** Route ID */
  routeId: string;
  /** Route name */
  routeName: string;
  /** Error messages */
  errors: string[];
  /** Warning messages */
  warnings: string[];
}

/**
 * Validation result structure
 */
export interface ValidationSummaryData {
  /** Whether validation passed */
  isValid: boolean;
  /** Overall error messages */
  errors: string[];
  /** Overall warning messages */
  warnings: string[];
  /** Success messages */
  successes?: string[];
  /** Per-route validation issues */
  routeIssues?: RouteValidationIssue[];
  /** Additional information */
  info?: string[];
}

/**
 * Props for ValidationSummary component
 */
export interface ValidationSummaryProps {
  /** Validation data to display */
  validation: ValidationSummaryData;
  /** Whether to show the summary by default */
  defaultExpanded?: boolean;
  /** Whether to show per-route details */
  showRouteDetails?: boolean;
  /** Custom title */
  title?: string;
  /** Callback when validation changes */
  onValidationChange?: (validation: ValidationSummaryData) => void;
}

/**
 * ValidationSummary Component
 * 
 * Displays validation results with color-coded status, counts, and expandable details.
 */
export const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  validation,
  defaultExpanded = false,
  showRouteDetails = true,
  title,
  onValidationChange,
}) => {
  const { t } = useTranslation('mapTransfer');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [routeDetailsExpanded, setRouteDetailsExpanded] = useState(false);

  // Calculate counts
  const errorCount = validation.errors.length;
  const warningCount = validation.warnings.length;
  const successCount = validation.successes?.length || 0;
  const routeIssueCount = validation.routeIssues?.length || 0;

  // Determine overall severity
  const severity: 'error' | 'warning' | 'success' | 'info' =
    errorCount > 0
      ? 'error'
      : warningCount > 0
      ? 'warning'
      : successCount > 0
      ? 'success'
      : 'info';

  // Determine status message
  const statusMessage =
    errorCount > 0
      ? t('routes.validation.errorsFound', { count: errorCount })
      : warningCount > 0
      ? t('routes.validation.warningsFound', { count: warningCount })
      : successCount > 0
      ? t('routes.validation.allPassed', { count: successCount })
      : t('routes.validation.complete');

  // Display title
  const displayTitle = title || t('routes.validation.title');

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity={severity}
        action={
          <IconButton
            aria-label={t('common.expandDetails')}
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <Icon size={20} name="chevron-up" /> : <Icon size={20} name="chevron-down" />}
          </IconButton>
        }
      >
        <AlertTitle>{displayTitle}</AlertTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Typography variant="body2">{statusMessage}</Typography>
          {errorCount > 0 && (
            <Chip
              icon={<Icon size={20} name="circle-exclamation" />}
              label={t('routes.validation.errorsChip', { count: errorCount })}
              color="error"
              size="small"
            />
          )}
          {warningCount > 0 && (
            <Chip
              icon={<Icon size={20} name="triangle-exclamation" />}
              label={t('routes.validation.warningsChip', { count: warningCount })}
              color="warning"
              size="small"
            />
          )}
          {successCount > 0 && (
            <Chip
              icon={<Icon size={20} name="circle-check" />}
              label={t('routes.validation.successesChip', { count: successCount })}
              color="success"
              size="small"
            />
          )}
          {routeIssueCount > 0 && (
            <Chip
              label={t('routes.validation.routesWithIssues', { count: routeIssueCount })}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {/* Expandable details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {/* Overall errors */}
            {errorCount > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {t('routes.validation.errorsHeading')}
                </Typography>
                <List dense>
                  {validation.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Icon name="circle-exclamation" size={20} />
                      </ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Overall warnings */}
            {warningCount > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {t('routes.validation.warningsHeading')}
                </Typography>
                <List dense>
                  {validation.warnings.map((warning, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Icon name="triangle-exclamation" size={20} />
                      </ListItemIcon>
                      <ListItemText primary={warning} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Successes */}
            {successCount > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {t('routes.validation.successesHeading')}
                </Typography>
                <List dense>
                  {validation.successes?.map((success, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Icon name="circle-check" size={20} />
                      </ListItemIcon>
                      <ListItemText primary={success} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Info messages */}
            {validation.info && validation.info.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="info.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {t('routes.validation.informationHeading')}
                </Typography>
                <List dense>
                  {validation.info.map((info, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Icon name="circle-info" size={20} />
                      </ListItemIcon>
                      <ListItemText primary={info} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Per-route details */}
            {showRouteDetails && routeIssueCount > 0 && (
              <Accordion
                expanded={routeDetailsExpanded}
                onChange={() => setRouteDetailsExpanded(!routeDetailsExpanded)}
                sx={{ mt: 2 }}
              >
                <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                  <Typography variant="subtitle2">
                    {t('routes.validation.perRouteDetails', { count: routeIssueCount })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {validation.routeIssues?.map((issue, index) => (
                      <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {t('routes.validation.routeHeading', { name: issue.routeName, id: issue.routeId })}
                        </Typography>
                        {issue.errors.length > 0 && (
                          <Box sx={{ ml: 2, mb: 1 }}>
                            {issue.errors.map((error, errorIndex) => (
                              <Box key={errorIndex} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Icon name="circle-exclamation" size={20} style={{ marginRight: 8 }} />
                                <Typography variant="body2" color="error">
                                  {error}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        {issue.warnings.length > 0 && (
                          <Box sx={{ ml: 2 }}>
                            {issue.warnings.map((warning, warningIndex) => (
                              <Box
                                key={warningIndex}
                                sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                              >
                                <Icon name="triangle-exclamation" size={20} style={{ marginRight: 8 }} />
                                <Typography variant="body2" color="warning.main">
                                  {warning}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Collapse>
      </Alert>
    </Box>
  );
};

